import * as React from 'react';
import { compose, setDisplayName, withHandlers, withState, lifecycle, withProps } from 'recompose';
import { translate } from 'react-i18next';
import { TranslateProps } from 'util/i18n';
import { connect } from 'react-redux';
import { State } from 'reducers';
import { ReportType, Report } from 'types';
import { Input } from 'ui/shared/Input';
import { Select } from 'ui/shared/Select';
import { setReportType, getReportNameTextKey, reportTypeSorter } from 'logic/reports/reportType';
import { getReportTypes } from 'selectors/serverSelectors';
import { ErrorMessage } from 'ui/shared/ErrorMessage';
import { Fade } from 'ui/transitions/Fade';

import './GeneralSection.css';

export type OwnProps = {
    report: Report;
    onChange: (r: Report) => void;
};

const RawGeneralSection = ({
    report,
    name,
    isNameErrorVisible,
    onChangeName,
    onBlurName,
    onChangeReportType,
    onChangeShared,
    reportTypes,
    t,
}: Props) =>
    <table id="edit-report-general-table">
        <tbody>
            <tr>
                <td>
                    { t('WC_REPORT_NAME') }
                </td>
                <td className="inline-child-divs">
                    <Input
                        id="edit-report-name"
                        value={ name }
                        onChange={ onChangeName }
                        onBlur={ onBlurName }
                    />
                    <Fade
                        visible={ isNameErrorVisible }
                        duration={ 200 }
                    >
                        <ErrorMessage
                            textKey="VAL_REQUIRED"
                        />
                    </Fade>
                </td>
            </tr>
            <tr>
                <td>
                    { t('6.1241.0.23') }
                </td>
                <td>
                    <Select
                        id="edit-report-type"
                        value={ report.reportType }
                        onChange={ onChangeReportType }
                    >
                        { reportTypes.map(reportType =>
                            <option
                                key={ reportType }
                                value={ reportType }
                            >
                                { t(getReportNameTextKey(reportType)) }
                            </option>
                        ) }
                    </Select>
                </td>
            </tr>
            <tr>
                <td>
                    { t('6.1241.0.25') }
                </td>
                <td>
                    <label>
                        <input
                            id="edit-report-shared"
                            type="checkbox"
                            checked={ report.shared }
                            onChange={ onChangeShared }
                        />
                        { t('6.1241.0.26') }
                    </label>
                </td>
            </tr>
        </tbody>
    </table>
;

type StateProps = {
    reportTypes: ReportType[];
};
type RecomposeProps = {
    name: string;
    isNameErrorVisible: boolean;
    onChangeName: React.ChangeEventHandler<HTMLInputElement>;
    onBlurName: React.FocusEventHandler<HTMLInputElement>;
    onChangeReportType: React.ChangeEventHandler<HTMLSelectElement>;
    onChangeShared: React.ChangeEventHandler<HTMLInputElement>;
};
type _RecomposeProps = RecomposeProps & {
    _setName: (s: string) => void;
    _setNameError: (s: string | null) => void;
    _setIsNameErrorVisible: (v: boolean) => void;
};

const mapStateToProps = (state: State) => ({
    reportTypes: getReportTypes(state),
});


type Props = OwnProps & StateProps & _RecomposeProps & TranslateProps;

export const GeneralSection = compose<Props, OwnProps>(
    setDisplayName('GeneralSection'),
    translate(),
    connect<StateProps, {}, OwnProps>(mapStateToProps),
    withProps<Partial<Props>, Props>(({ reportTypes, t }) => ({
        reportTypes: reportTypes.sort(reportTypeSorter(t)),
    })),
    withState('name', '_setName', ({ report }: OwnProps) =>
        report.name || ''
    ),
    withState('isNameErrorVisible', '_setIsNameErrorVisible', ({ name }: RecomposeProps) =>
        name.trim().length === 0
    ),
    withHandlers<Props, Partial<RecomposeProps>>({
        onChangeName: ({ _setName, _setIsNameErrorVisible }) => (e: React.ChangeEvent<HTMLInputElement>) => {
            const name = e.target.value;
            _setName(name);
            if (name) {
                // Hide the error if the user starts typing (but don't show error until blur)
                _setIsNameErrorVisible(false);
            }
        },
        onBlurName: ({ report, onChange, name, _setNameError, _setIsNameErrorVisible, t }) => (e: React.FocusEvent<HTMLInputElement>) => {
            name = name.trim();
            _setIsNameErrorVisible(!name.length);
            onChange({ ...report, name });
        },
        onChangeReportType: ({ report, onChange }) => (e: React.ChangeEvent<HTMLSelectElement>) => {
            const reportType = e.target.value as ReportType;
            report = setReportType(report, reportType);
            onChange({ ...report, reportType });
        },
        onChangeShared: ({ report, onChange }) => (e: React.ChangeEvent<HTMLInputElement>) => {
            const shared = e.target.checked;
            onChange({ ...report, shared });
        },
    }),
    lifecycle<Props, {}>({
        componentDidUpdate(oldProps: Props) {
            const { report, _setName, _setIsNameErrorVisible } = this.props;
            if (report.reportId !== oldProps.report.reportId) {
                _setName(report.name);
                _setIsNameErrorVisible(!report.name.length);
            }
        },
    }),
)(RawGeneralSection);



// WEBPACK FOOTER //
// ./src/ui/pages/designer/sections/general/GeneralSection.tsx